import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Stack,
  Snackbar,
  Alert,
  IconButton,
  Divider,
  LinearProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Edit, Save, Close, Add, Delete } from "@mui/icons-material";
import { styled } from "@mui/system";
import apiEndpoints from "../../apiconfig";

// Styled Components (keep the same as before)
const GradientCard = styled(Card)({
  borderRadius: "16px",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  display: "flex",
  flexDirection: "column",
  height: "500px",
  width: "100%",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
  },
  overflow: "hidden",
});

const ScrollableContent = styled(Box)({
  overflowY: "auto",
  flex: 1,
  paddingRight: "8px",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "rgba(0,0,0,0.05)",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(0, 184, 148, 0.3)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "rgba(0, 184, 148, 0.5)",
  },
});

const ContentBox = styled(Box)({
  backgroundColor: "rgba(0, 184, 148, 0.05)",
  borderRadius: "10px",
  padding: "12px",
  margin: "8px 0",
  border: "1px solid rgba(0, 184, 148, 0.1)",
  whiteSpace: "pre-wrap",
  overflow: "auto",
  maxHeight: "150px",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    "& fieldset": {
      borderColor: "rgba(0, 184, 148, 0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0, 184, 148, 0.4)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#00b894",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(0, 0, 0, 0.6)",
    "&.Mui-focused": {
      color: "#00b894",
    },
  },
});

function TermsAndConditions() {
  const [form, setForm] = useState({
    title: "Terms & Conditions",
    effectiveDate: "05-06-2025",
    organization: "Devi Chandran Public Welfare Trust",
    website: "www.dcpwtrust.com",
    introduction: "This website is owned and operated by Devi Chandran Public Welfare Trust (\"we\", \"us\", or \"our\"). By accessing this website, you agree to comply with and be bound by the following Terms and Conditions, our Privacy Policy, and other policies provided herein.",
    termsItems: [
      {
        id: 1,
        heading: "Use of the Website",
        contents: ["You agree to use this website for lawful and personal/non-commercial purposes only. You shall not use this website in any manner that could damage or impair its functioning or content."]
      },
      {
        id: 2,
        heading: "Privacy Policy",
        contents: ["By using this website, you consent to the collection and use of your information as described in our Privacy Policy."]
      },
      {
        id: 3,
        heading: "Accuracy of Information",
        contents: ["All information is provided for general purposes only. Devi Chandran Public Welfare Trust does not guarantee the completeness or accuracy of content on this website. You are responsible for reviewing and verifying any information before acting upon it."]
      },
      {
        id: 4,
        heading: "Intellectual Property",
        contents: ["All content including text, logos, graphics, downloads, and media are property of Devi Chandran Public Welfare Trust. You may not reproduce, publish, distribute, or use our content for any commercial purpose without our written permission."]
      },
      {
        id: 5,
        heading: "Modifications to Terms",
        contents: ["We reserve the right to update or revise these Terms at any time. Continued use of the website constitutes your acceptance of any changes made."]
      }
    ],
    email: "Support@dcpwtrust.com",
    address: "Kanchipuram, Tamil Nadu"
  });

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [editItemIndex, setEditItemIndex] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState({
    heading: "",
    contents: [""],
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const fetchTermsData = async () => {
    setLoading(true);
    try {
      const response = await fetch(apiEndpoints.termsconditions);
      if (!response.ok) {
        throw new Error("Failed to fetch terms data");
      }

      const data = await response.json();

      if (data && !data.error) {
        setForm({
          title: data.title || "Terms & Conditions",
          effectiveDate: data.effective_date || "05-06-2025", // Map to effectiveDate
          organization: data.organization || "Devi Chandran Public Welfare Trust",
          website: data.website || "www.dcpwtrust.com",
          introduction: data.introduction || "This website is owned and operated by...",
          termsItems: data.terms_items || [ // Map to termsItems
            {
              id: 1,
              heading: "Use of the Website",
              contents: ["You agree to use this website for lawful purposes..."]
            }
          ],
          email: data.email || "Support@dcpwtrust.com",
          address: data.address || "Kanchipuram, Tamil Nadu"
        });
      } else if (data.error) {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error("Error fetching terms:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to load terms",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateTermsData = async (formData) => {
    setLoading(true);
    try {
      // Transform the data to match backend expectations
      const payload = {
        title: formData.title,
        effective_date: formData.effectiveDate, // Changed from effectiveDate
        organization: formData.organization,
        website: formData.website,
        introduction: formData.introduction,
        terms_items: formData.termsItems, // Changed from termsItems
        email: formData.email,
        address: formData.address
      };

      const response = await fetch(apiEndpoints.termsconditions, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update terms");
      }

      const result = await response.json();
      setSnackbar({
        open: true,
        message: result.message || "Terms updated successfully!",
        severity: "success",
      });
      return result;
    } catch (error) {
      console.error("Error updating terms:", error);
      setSnackbar({
        open: true,
        message: error.message || "Error updating terms",
        severity: "error",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTermsData();
  }, []);

  const handleAddContentField = () => {
    setDialogData({
      ...dialogData,
      contents: [...dialogData.contents, ""],
    });
  };

  const handleContentChange = (index, value) => {
    const newContents = [...dialogData.contents];
    newContents[index] = value;
    setDialogData({
      ...dialogData,
      contents: newContents,
    });
  };

  const handleAddItem = () => {
    if (dialogData.heading.trim() && dialogData.contents.some((c) => c.trim())) {
      const newItem = {
        id: Date.now(),
        heading: dialogData.heading,
        contents: dialogData.contents.filter((c) => c.trim()),
      };
      setForm({ ...form, termsItems: [...form.termsItems, newItem] });
      setDialogData({ heading: "", contents: [""] });
      setDialogOpen(false);
    }
  };

  const handleEditItem = () => {
    if (editItemIndex !== null && dialogData.heading.trim() && dialogData.contents.some((c) => c.trim())) {
      const updatedItems = [...form.termsItems];
      updatedItems[editItemIndex] = {
        ...updatedItems[editItemIndex],
        heading: dialogData.heading,
        contents: dialogData.contents.filter((c) => c.trim()),
      };
      setForm({ ...form, termsItems: updatedItems });
      setEditItemIndex(null);
      setDialogData({ heading: "", contents: [""] });
      setDialogOpen(false);
    }
  };

  const handleDeleteItem = (index) => {
    const updatedItems = [...form.termsItems];
    updatedItems.splice(index, 1);
    setForm({ ...form, termsItems: updatedItems });
  };

  const openEditDialog = (index) => {
    setEditItemIndex(index);
    const item = form.termsItems[index];
    setDialogData({
      heading: item.heading,
      contents: item.contents.length > 0 ? item.contents : [""],
    });
    setDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditItemIndex(null);
    setDialogData({ heading: "", contents: [""] });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateTermsData(form);
      setEditing(false);
    } catch {
      setSnackbar({
        open: true,
        message: "Error updating terms.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f8fafc" }}>
      <Box sx={{ width: "100%" }}>
        <Grid container width={"100%"} maxWidth={"100%"}>
          <Grid item xs={12} width={"100%"}>
            <GradientCard>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  width: "100%",
                  paddingBottom: "0",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    width: "100%",
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Terms and Conditions Editor
                  </Typography>
                  <IconButton
                    onClick={() => setEditing(!editing)}
                    color="primary"
                    sx={{
                      backgroundColor: editing ? "#ff4444" : "#00b894",
                      color: "white",
                      "&:hover": {
                        backgroundColor: editing ? "#cc0000" : "#009975",
                      },
                    }}
                  >
                    {editing ? <Close /> : <Edit />}
                  </IconButton>
                </Box>

                <Divider sx={{ my: 2, flexShrink: 0 }} />

                <ScrollableContent>
                  <form onSubmit={handleSubmit}>
                    <Stack spacing={2} sx={{ pb: 2 }}>
                      {/* Title */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Title
                        </Typography>
                        {editing ? (
                          <StyledTextField
                            name="title"
                            value={form.title}
                            onChange={handleChange}
                            fullWidth
                          />
                        ) : (
                          <ContentBox>
                            <Typography variant="h6">{form.title}</Typography>
                          </ContentBox>
                        )}
                      </Box>

                      {/* Effective Date */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Effective Date
                        </Typography>
                        {editing ? (
                          <StyledTextField
                            name="effectiveDate"
                            value={form.effectiveDate}
                            onChange={handleChange}
                            fullWidth
                          />
                        ) : (
                          <ContentBox>
                            <Typography>Effective Date: {form.effectiveDate}</Typography>
                          </ContentBox>
                        )}
                      </Box>

                      {/* Organization */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Organization
                        </Typography>
                        {editing ? (
                          <StyledTextField
                            name="organization"
                            value={form.organization}
                            onChange={handleChange}
                            fullWidth
                          />
                        ) : (
                          <ContentBox>
                            <Typography>Organization: {form.organization}</Typography>
                          </ContentBox>
                        )}
                      </Box>

                      {/* Website */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Website
                        </Typography>
                        {editing ? (
                          <StyledTextField
                            name="website"
                            value={form.website}
                            onChange={handleChange}
                            fullWidth
                          />
                        ) : (
                          <ContentBox>
                            <Typography>Website: {form.website}</Typography>
                          </ContentBox>
                        )}
                      </Box>

                      {/* Introduction */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Introduction
                        </Typography>
                        {editing ? (
                          <StyledTextField
                            name="introduction"
                            value={form.introduction}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={3}
                          />
                        ) : (
                          <ContentBox>
                            <Typography>{form.introduction}</Typography>
                          </ContentBox>
                        )}
                      </Box>

                      {/* Terms Sections */}
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight={600}>
                            Terms Sections
                          </Typography>
                          {editing && (
                            <Button
                              onClick={openAddDialog}
                              startIcon={<Add />}
                              size="small"
                              sx={{
                                backgroundColor: "#00b894",
                                "&:hover": { backgroundColor: "#009975" },
                                borderRadius: "8px",
                                textTransform: "none",
                              }}
                            >
                              Add Section
                            </Button>
                          )}
                        </Box>
                        {editing ? (
                          <Box
                            sx={{
                              backgroundColor: "rgba(0, 184, 148, 0.05)",
                              borderRadius: "10px",
                              padding: "12px",
                              border: "1px solid rgba(0, 184, 148, 0.1)",
                            }}
                          >
                            {form.termsItems?.map((item, index) => (
                              <Box
                                key={item.id}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  mb: 2,
                                  p: 1,
                                  backgroundColor: "rgba(255,255,255,0.7)",
                                  borderRadius: "8px",
                                }}
                              >
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ flexGrow: 1, fontWeight: 600 }}
                                  >
                                    {item.heading}
                                  </Typography>
                                  <IconButton
                                    onClick={() => openEditDialog(index)}
                                    size="small"
                                    sx={{ color: "#00b894" }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => handleDeleteItem(index)}
                                    size="small"
                                    sx={{ color: "#ff4444" }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Box>
                                {item.contents?.map((content, i) => (
                                  <Typography key={i} sx={{ pl: 2, mt: 1 }}>
                                    • {content}
                                  </Typography>
                                ))}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <ContentBox>
                            {form.termsItems?.map((item) => (
                              <Box key={item.id} sx={{ mb: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={600}
                                >
                                  {item.heading}
                                </Typography>
                                {item.contents?.map((content, i) => (
                                  <Box
                                    key={i}
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      mt: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: "6px",
                                        height: "6px",
                                        backgroundColor: "#00b894",
                                        borderRadius: "50%",
                                        mt: 1.5,
                                        mr: 1.5,
                                      }}
                                    />
                                    <Typography>{content}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            ))}
                          </ContentBox>
                        )}
                      </Box>

                      {/* Contact Email */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Contact Email
                        </Typography>
                        {editing ? (
                          <StyledTextField
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            fullWidth
                            type="email"
                          />
                        ) : (
                          <ContentBox>
                            <Typography>📧 Email: {form.email}</Typography>
                          </ContentBox>
                        )}
                      </Box>

                      {/* Address */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Address
                        </Typography>
                        {editing ? (
                          <StyledTextField
                            name="address"
                            value={form.address}
                            onChange={handleChange}
                            fullWidth
                            type="text"
                          />
                        ) : (
                          <ContentBox>
                            <Typography>📍 Address: {form.address}</Typography>
                          </ContentBox>
                        )}
                      </Box>
                    </Stack>
                  </form>
                </ScrollableContent>

                {editing && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      pt: 2,
                      pb: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Button
                      onClick={() => setEditing(false)}
                      variant="outlined"
                      color="error"
                      startIcon={<Close />}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      variant="contained"
                      sx={{
                        backgroundColor: "#00b894",
                        "&:hover": { backgroundColor: "#009975" },
                      }}
                      startIcon={<Save />}
                    >
                      Save
                    </Button>
                  </Box>
                )}
              </CardContent>
              {loading && (
                <LinearProgress
                  sx={{
                    height: 4,
                    "& .MuiLinearProgress-bar": { backgroundColor: "#00b894" },
                  }}
                />
              )}
            </GradientCard>
          </Grid>
        </Grid>
      </Box>

      {/* Dialog for adding/editing terms sections */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": {
            maxHeight: "80vh",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle>
          {editItemIndex !== null
            ? "Edit Terms Section"
            : "Add New Terms Section"}
        </DialogTitle>
        <DialogContent dividers sx={{ padding: "16px", overflowY: "auto" }}>
          <Stack spacing={2} sx={{ height: "100%" }}>
            <TextField
              autoFocus
              margin="dense"
              label="Section Heading"
              fullWidth
              value={dialogData.heading}
              onChange={(e) =>
                setDialogData({ ...dialogData, heading: e.target.value })
              }
              sx={{ mb: 2 }}
            />

            <Box
              sx={{
                maxHeight: "300px",
                overflowY: "auto",
                pr: 1,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "rgba(0,0,0,0.05)",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(0, 184, 148, 0.3)",
                  borderRadius: "3px",
                },
              }}
            >
              {dialogData.contents?.map((content, index) => (
                <TextField
                  key={index}
                  margin="dense"
                  label={`Content ${index + 1}`}
                  fullWidth
                  multiline
                  rows={3}
                  value={content}
                  onChange={(e) => handleContentChange(index, e.target.value)}
                  sx={{ mb: 2 }}
                />
              ))}
            </Box>

            <Button
              onClick={handleAddContentField}
              startIcon={<Add />}
              variant="outlined"
              color="primary"
              sx={{ mt: 1 }}
            >
              Add Content Field
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <Button
            onClick={() => setDialogOpen(false)}
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={editItemIndex !== null ? handleEditItem : handleAddItem}
            disabled={
              !dialogData.heading.trim() ||
              !dialogData.contents.some((c) => c.trim())
            }
            variant="contained"
            sx={{
              backgroundColor: "#00b894",
              "&:hover": { backgroundColor: "#009975" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TermsAndConditions;